import React, { useState } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { ProgressCircular } from '@americanexpress/dls-react';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { IntlProvider } from 'react-intl';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { MODULE_NAME, MOSAIC_GET_INSPIRED_EXPERIMENT, PLACEMENT_IDS } from '../constants/moduleConstants';
import { mockHeading, mockArticleSets } from '../../__data__/mockArticleData';
import type { MockArticleSetKey, TravelPersonalizedMarketingArticlesType } from '../types/PersonalizedMarketingArticles';
import { loadModuleData } from '../thunks/loadModuleData';
import { ArticleLayout } from './ArticleLayout/ArticleLayout';
import styles from './MarketingArticleStyles.css';
import '@americanexpress/consumer-travel-base/build/index.css';
import { useArticles } from '../hooks/useArticles';
import { filterArticles } from '../utils/articles';

const PersonalizedMarketingArticles: TravelPersonalizedMarketingArticlesType = ({
  languageData,
  heading,
  articles,
  isParentDataLoading,
  useMockData = false,
  analyticsElementPrefix,
  clientId,
  placementId,
}) => {
  /* istanbul ignore next - used for local testing on demo route only */
  const [selectedMockSet, setSelectedMockSet] = useState<MockArticleSetKey>('Four Articles');
  /* istanbul ignore next - local testing */
  heading = useMockData ? mockHeading : heading;
  /* istanbul ignore next - local testing */
  articles = filterArticles(useMockData ? mockArticleSets[selectedMockSet] : articles);
  /* istanbul ignore next - local testing */
  placementId = selectedMockSet.includes('Offer') ? PLACEMENT_IDS.HOTEL_OFFERS_PLACEMENT : placementId;

  /* istanbul ignore next - local testing */
  const localTestingComponent = useMockData ? (
    <div className="flex flex-justify-center pad-t">
      <label htmlFor="mock-articles-select">Mock article set:</label>
      <select
        id="mock-articles-select"
        value={selectedMockSet}
        onChange={(event) => setSelectedMockSet(event.target.value as MockArticleSetKey)}
      >
        {Object.keys(mockArticleSets).map((key) => <option key={key} value={key}>{key}</option>)}
      </select>
    </div>
  ) : null;

  const { isLoading: oneDataArticlesLoading } = useArticles({ clientId, placementId });
  if (isParentDataLoading || oneDataArticlesLoading) {
    return (
      <IntlProvider locale={languageData.locale} messages={languageData}>
        <div className={`container flex flex-justify-center flex-align-center ${styles.progressWrapper}`}>
          <ProgressCircular size="lg" />
        </div>
      </IntlProvider>
    );
  }

  const renderArticleLayout = (isExperiment: boolean) => (
    <ArticleLayout
      heading={heading}
      analyticsElementPrefix={analyticsElementPrefix}
      clientId={clientId}
      placementId={placementId}
      articles={filterArticles(articles)}
      isExperiment={isExperiment}
    />
  );

  return (
    <IntlProvider locale={languageData.locale} messages={languageData}>
      <ExtendReactlytics analytics={{ event: { identifier: MODULE_NAME } }}>
        <div
          id={`${analyticsElementPrefix}-articles`}
          data-testid="personalized-marketing-articles"
          className={`container ${styles.marketingArticlesWrapper}`}
        >
          <Experiment experimentName={MOSAIC_GET_INSPIRED_EXPERIMENT} defaultVariant="control">
            <Variant variant="control">{renderArticleLayout(false)}</Variant>
            <Variant variant="user">{renderArticleLayout(true)}</Variant>
          </Experiment>
        </div>
        { localTestingComponent }
      </ExtendReactlytics>
    </IntlProvider>
  );
};

export const TestableTravelPersonalizedMarketingArticles = PersonalizedMarketingArticles;

PersonalizedMarketingArticles.holocron = {
  name: MODULE_NAME,
  loadModuleData,
};

export default oneAppModuleWrapper(MODULE_NAME)(PersonalizedMarketingArticles);
